import type { ArmCombination } from '~/features/configuration/types/treatment-configuration.types';
import { combinationForArms } from '~/features/configuration/utils/arm-combinations';

import type {
  ContentAttribute,
  CreateExperimentWizardState,
  ExperimentChannel,
  GroupingMode,
  ProductGroup,
  TestDuration,
  WizardStep,
} from '../types/create-experiment.types';
import { DEFAULT_VISIBLE_COLUMNS } from '../types/create-experiment.types';
import type { DraftExperiment } from '../types/experiment-draft.types';
import type { AppliedProductFilters } from '../types/product-metadata.types';

import { omitAppliedFilter } from './applied-filters';
import { hydrateArmSelections } from './arm-content-attributes';
import { EXPERIMENT_POST_STEP_DEFAULTS } from './content-attribute-steps';
import { stageToStep } from './stage-to-step';

/**
 * Fallback channel used when the GET response omits `channel` — applied
 * during the rollout window where backend may not yet persist it. Logged
 * via Sentry breadcrumb at the call-site so we can confirm the cleanup is
 * safe before removing the fallback.
 */
const FALLBACK_CHANNEL_CODE = 'GOOGLE_MERCHANT';

interface HydrateInput {
  /** Raw draft wire DTO from `GET /experiments/:id`. */
  dto: DraftExperiment;
  /** Content-attribute catalog — needed to inflate `string[]` to the nested store shape. */
  contentAttributes: ContentAttribute[] | undefined;
  /**
   * Connection id fallback when the wire DTO doesn't carry `channel` — the
   * caller resolves it via `useConnectedConnectors` filtered by the fallback
   * code. `null` when no Google connection exists for the tenant; resume
   * surfaces an error in that case.
   */
  fallbackConnectionId: string | null;
}

/**
 * Convert a draft wire DTO into the wizard store's partial state. Pure —
 * caller dispatches the result to `useCreateExperimentStore.hydrate`. Each
 * slice falls back to its initial-state defaults when the corresponding
 * wire field is missing or null, so partial drafts still hydrate cleanly.
 *
 * Conversions applied:
 * - `scopingFilters`/`groupingFilter.filters` passed through as-is (API returns `AppliedProductFilters`).
 * - `armContentAttributes` (falling back to `contentAttributes`) → one nested selection per enriched arm via catalog lookup.
 * - `stage` → `currentStep` via `stageToStep` (unknown → 1).
 *
 * Channel fallback: when `dto.channel` is absent, default to
 * `{ code: FALLBACK_CHANNEL_CODE, connectionId: fallbackConnectionId }` so
 * downstream catalogs (KPIs, columns) can still resolve. Returns `null` for
 * channel only when neither source is available — wizard then surfaces the
 * missing-channel state.
 */
export function hydrateFromDraft(input: HydrateInput): Partial<CreateExperimentWizardState> {
  const { dto, contentAttributes, fallbackConnectionId } = input;

  const currentStep = stageToStep(dto.stage);
  const completedSteps = buildCompletedSteps(currentStep);

  // -- Channel ---------------------------------------------------------
  const channel = resolveChannel(dto, fallbackConnectionId);

  // -- Step 1 (Define Product Groups) ---------------------------------
  const step1 = {
    method: dto.parentIdField || null,
    manualRule: '',
    // Past Step 1 → preview is implicitly fetched. Fresh drafts at Step 1
    // start without preview so the chat panel re-fetches.
    previewFetched: currentStep > 1,
  };

  // -- Step 2 (Define Scope) ------------------------------------------
  // A `product_id` array in the saved scope means the draft was scoped via the
  // A candidate-scope file is restored from the experiment's own
  // `offerIdsTotal`, not from `scopingFilters` — the file is the scope, and the
  // ids are never round-tripped through the filter map.
  //
  // `product_id` is still stripped from the filters for drafts created BEFORE
  // that change: those carry the uploaded ids as a filter, and leaving them
  // there would filter the preview on the wrong column (`externalId`) and
  // render hundreds of id chips.
  const scoping = dto.scopingFilters ?? {};
  const hasLegacyIdFilter = Array.isArray(scoping.product_id);
  const uploadCount = dto.offerIdsTotal ?? null;
  const step2 = {
    search: '',
    page: 1,
    pageSize: 10,
    appliedFilters: hasLegacyIdFilter ? omitAppliedFilter(scoping, 'product_id') : scoping,
    visibleColumns: [...DEFAULT_VISIBLE_COLUMNS],
    // `offerIdsType` is the only place a resumed scope's identifier survives —
    // without it an item-group scope would be labelled "offer ids". Falls back
    // to the documented default for drafts stored before the field existed.
    uploadIdType: dto.offerIdsType ?? ('OFFER_ID' as const),
    uploadedIdType: uploadCount === null ? null : (dto.offerIdsType ?? 'OFFER_ID'),
    uploadCount,
    // A resumed draft has the count but not the ids — the file lives in GCS
    // behind an opaque URI, so the preview can't be narrowed until the operator
    // re-uploads. Empty rather than absent so the card can say why.
    uploadPreviewIds: [],
    uploadFileName: null,
  };

  // -- Step 3 (Analytics Setup / Grouping) -----------------------------
  const step3 = buildStep3(dto);

  // -- Step 4 (Experiment Strategy) -----------------------------------
  const step4 = {
    name: dto.name ?? '',
    primaryKpi: dto.primaryKpi,
    secondaryKpi: dto.secondaryKpi,
    armCombination: coerceArmCombination(dto.arms, dto.numberOfArms),
    // Per-arm treatment ids aren't persisted on the draft yet (experiment API
    // pending) — start null so the Strategy step re-derives them from Treatment
    // Configuration on resume.
    armBTreatmentId: null,
    armCTreatmentId: null,
    testDuration: coerceTestDuration(dto.testDurationDays),
    // Per-arm map when saved; otherwise every arm falls back to the single
    // legacy `contentAttributes` selection.
    armContentAttributes: hydrateArmSelections(dto, contentAttributes),
    // Scoring pinned off, as on a fresh wizard. The descriptor flags are derived
    // from the attribute selection above, which the draft DOES round-trip, so resume
    // recovers those without needing a field of their own.
    postStepFlags: EXPERIMENT_POST_STEP_DEFAULTS,
    // The uri itself is opaque and never displayed — its presence is the whole
    // signal: the Strategy step can then say a file is attached instead of
    // showing an empty dropzone over a draft that has one.
    hasStoredArmAssignmentFile: (dto.armAssignmentFileUri ?? null) !== null,
    retailer: dto.retailer ?? '',
    targetLocales: dto.targetLocales ?? [],
  };

  return {
    channel,
    draftId: dto.id,
    currentStep,
    completedSteps,
    step1,
    step2,
    step3,
    step4,
  };
}

/** Resolve `channel` from the DTO with the rollout-window fallback applied. */
function resolveChannel(
  dto: DraftExperiment,
  fallbackConnectionId: string | null,
): ExperimentChannel | null {
  if (dto.channel?.connectionId && dto.channel.code) {
    return { connectionId: dto.channel.connectionId, code: dto.channel.code };
  }
  if (fallbackConnectionId) {
    return { connectionId: fallbackConnectionId, code: FALLBACK_CHANNEL_CODE };
  }
  return null;
}

/** Steps strictly before the landing step are considered "completed". */
function buildCompletedSteps(target: WizardStep): number[] {
  const out: number[] = [];
  for (let i = 1; i < target; i++) out.push(i);
  return out;
}

/**
 * Hydrate Step 3 (grouping). Mode is derived from three wire shapes:
 *   - custom group (`groupingFilter.name` non-null) → `'custom'`.
 *   - "no grouping" → `'none'`. This is a `null` `groupingFilter` on drafts
 *     that have advanced through step 3 (new writes), OR the legacy
 *     `{ name: null, filters }` sentinel (older drafts that echoed the scope).
 *   - `null` `groupingFilter` on a draft that hasn't reached step 3 yet
 *     (`stage` still at/before step 2) → `null` (undecided).
 * The `null`-vs-`'none'` disambiguation uses `stage`: only a completed step 3
 * carries `stage` = `ANALYTICS_SETUP` or later.
 *
 * For `custom` mode, `step3.appliedFilters` is hydrated to the same slice
 * the group was created from. The create-flow invariant is that at the
 * moment of "Create Group", `appliedFilters === group.filters` — so on
 * resume we restore the chip row + drawer state to match the group, which
 * keeps the UI consistent with the group card and lets the user edit/remove
 * filters from where they left off.
 */
/**
 * Re-runnable step-3 builder. Used by the resume loader on initial mount
 * AND by `ProductGroupingScreen` itself to re-hydrate the step-3 slice
 * whenever the screen is re-entered, so unsaved local edits don't drift
 * away from what the API draft actually persists.
 */
export function buildStep3(dto: DraftExperiment): {
  mode: GroupingMode | null;
  group: ProductGroup | null;
  appliedFilters: AppliedProductFilters;
} {
  const gf = dto.groupingFilter;
  if (gf && gf.name !== null) {
    const groupFilters = gf.filters;
    // The group's count is NOT `dto.skuCount` — that is the scope total
    // (products matching `scopingFilters`), not this reporting cut. Seed from
    // the legacy nested per-group count when present (older drafts), else 0;
    // `ProductGroupingScreen`'s live-sync recomputes the true group total from
    // the product list on mount. Drop the legacy fallback once fully rolled out.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- intentional legacy-draft fallback during rollout
    const legacySkuCount = gf.skuCount;
    const group: ProductGroup = {
      id: `grp-${dto.id}`,
      name: gf.name,
      filters: groupFilters,
      skuCount: legacySkuCount ?? 0,
    };
    return { mode: 'custom', group, appliedFilters: groupFilters };
  }
  // No custom group. Treat as an explicit "no grouping" decision when either
  // the legacy sentinel is present (`gf = { name: null, filters }`) OR the
  // draft has advanced through step 3 (new writes persist `groupingFilter:
  // null`). Any scope filters the legacy sentinel echoed are already restored
  // as `step2.appliedFilters`, so step 3's additive slice stays empty and the
  // chip row doesn't double up.
  if (gf || stageToStep(dto.stage) >= 3) {
    return { mode: 'none', group: null, appliedFilters: {} };
  }
  // `null` grouping filter before step 3 was reached — the user hasn't decided.
  return { mode: null, group: null, appliedFilters: {} };
}

/**
 * Resolve the draft's arm combination. Prefers the explicit `arms` array; falls
 * back to `numberOfArms` (TOTAL arms incl. control) for drafts saved before
 * `arms` existed — 2 ⇒ Control-vs-B (the only 2-arm shape that was then
 * expressible), 3 ⇒ Control-vs-B-vs-C. Anything else (incl. legacy 1-arm rows
 * persisted before the boundary translation landed) resets to null so the form
 * re-prompts rather than guessing.
 */
function coerceArmCombination(
  arms: ('A' | 'B' | 'C')[] | null | undefined,
  numberOfArms: number | null | undefined,
): ArmCombination | null {
  const fromArms = combinationForArms(arms ?? undefined);
  if (fromArms) return fromArms;
  if (numberOfArms === 2) return 'A_B';
  if (numberOfArms === 3) return 'A_B_C';
  return null;
}

/** Coerce wire `testDurationDays` to the store's `TestDuration` union; unknown values reset to null. */
function coerceTestDuration(value: number | null | undefined): TestDuration | null {
  if (value === 28 || value === 45 || value === 60 || value === 90 || value === 180) {
    return value;
  }
  return null;
}
